//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import type { ContentInspirationUploadRecord } from '@campaigns/hooks/contentInspirationUploadTypes';

type UploadStoreListener = () => void;

interface UploadStoreState {
  contentInspirationUploadRecords: ContentInspirationUploadRecord[];
  pendingContentInspirationUploadCount: number;
}

type CampaignRequestId = string | number;

const EMPTY_UPLOAD_STATE: UploadStoreState = {
  contentInspirationUploadRecords: [],
  pendingContentInspirationUploadCount: 0,
};

const createUploadStore = () => {
  const states = new Map<string, UploadStoreState>();
  const keysToClearWhenIdle = new Set<string>();
  const listeners = new Set<UploadStoreListener>();

  const notify = () => {
    listeners.forEach((listener) => listener());
  };

  const getState = (campaignRequestId: CampaignRequestId | undefined) => {
    if (campaignRequestId === undefined) {
      return EMPTY_UPLOAD_STATE;
    }

    return states.get(String(campaignRequestId)) ?? EMPTY_UPLOAD_STATE;
  };

  const setState = (
    campaignRequestId: CampaignRequestId,
    partial: Partial<UploadStoreState>,
  ) => {
    const key = String(campaignRequestId);
    const nextState = { ...getState(campaignRequestId), ...partial };
    if (
      nextState.pendingContentInspirationUploadCount === 0 &&
      keysToClearWhenIdle.has(key)
    ) {
      states.delete(key);
      keysToClearWhenIdle.delete(key);
    } else {
      states.set(key, nextState);
    }
    notify();
  };

  const clearState = (campaignRequestId: CampaignRequestId) => {
    const key = String(campaignRequestId);
    states.delete(key);
    keysToClearWhenIdle.delete(key);
    notify();
  };

  const clearStateWhenIdle = (campaignRequestId: CampaignRequestId) => {
    const key = String(campaignRequestId);
    if (
      getState(campaignRequestId).pendingContentInspirationUploadCount === 0
    ) {
      clearState(campaignRequestId);
      return;
    }

    keysToClearWhenIdle.add(key);
  };

  const subscribe = (listener: UploadStoreListener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  };

  return {
    clearState,
    clearStateWhenIdle,
    getState,
    setState,
    subscribe,
  };
};

export const contentInspirationUploadStore = createUploadStore();
